import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, streamChat } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { isViewer } from "@/lib/guards";
import AppHeader from "@/components/AppHeader";
import VoiceOrb from "@/components/VoiceOrb";
import Markdown from "@/components/Markdown";
import MessageBubble from "@/components/shared/MessageBubble";
import ArtifactActions from "@/components/shared/ArtifactActions";
import ChatComposer from "@/components/shared/ChatComposer";
import { docsFromMessages, messageFromAttach, promptToUseDoc } from "@/lib/attach";
import ArtifactStage, { ArtifactToolbar } from "@/components/shared/ArtifactStage";
import TablesBar from "@/components/shared/TablesBar";
import useThreadAttach from "@/hooks/useThreadAttach";
import AudioFirstOverlay, { VoiceHelpAction } from "@/components/shared/AudioFirstOverlay";
import { ErrorState, CardGridSkeleton } from "@/components/shared/StateViews";
import AvatarPickerSheet from "@/components/agent/AvatarPickerSheet";
import { publicAvatarDescription } from "@/lib/avatarUi";
import CarouselArtifact from "@/components/agent/CarouselArtifact";
import RequiredFieldsDrawer from "@/components/agent/RequiredFieldsDrawer";
import useVoiceSession from "@/hooks/useVoiceSession";
import { startAvatarCreation } from "@/lib/avatarFlow";
import { downloadArtifactPdf } from "@/lib/api";
import { friendlyError, errorCode } from "@/lib/errors";
import {
  Tabs, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu";
import { Plus, History, Repeat, Search, FileText, CalendarPlus, MoreVertical, Pencil, FolderInput, FolderPlus, FolderMinus, Trash2, Folder } from "lucide-react";
import AddToCalendarSheet from "@/components/calendar/AddToCalendarSheet";
import { extractInstagramCaption } from "@/lib/caption";
import { isSavedWithRevision, pdfFallbackName, workingView } from "@/lib/tables";
import { toast } from "sonner";

// Mirror of the backend plan gate (GET /agents `locked_plan` / POST /threads 403).
const PLAN_RANK = { free: 0, pro: 1, premium: 2 };
const LOCKED_PLAN_MSG = "Este agente faz parte de um plano que ainda não está liberado na sua conta.";

export default function AgentChat() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const viewer = isViewer(user);
  // Deep-link params (Epic 15.4): ?thread selects a specific conversation,
  // ?autosend fires one message as soon as the thread is ready, ?entry links the
  // next artifact back to a calendar entry. Captured once in refs so the params
  // can be cleared from the URL without losing the intent.
  const threadParamRef = useRef(searchParams.get("thread"));
  const autosendRef = useRef(searchParams.get("autosend"));
  const entryRef = useRef(searchParams.get("entry"));
  const autosentRef = useRef(false);
  const entryLinkedRef = useRef(false);
  const scheduledRef = useRef(false);
  // Mensagem digitada enquanto a IA responde: fica aqui e sai sozinha no fim.
  const queuedRef = useRef(null);
  const abortRef = useRef(null);
  // Cada navegação (carga, "Nova conversa", clique na lista) ganha um número; respostas
  // de uma navegação antiga não aplicam estado (StrictMode, cliques rápidos).
  const navSeqRef = useRef(0);
  const activeThreadRef = useRef(null);
  const [agent, setAgent] = useState(null);
  const [prereq, setPrereq] = useState(null);
  const [threads, setThreads] = useState([]);
  const [activeThread, setActiveThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [artifact, setArtifact] = useState(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [queued, setQueued] = useState(false);
  const [savingArtifactId, setSavingArtifactId] = useState(null);
  const [orbState, setOrbState] = useState("idle");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [prereqLoading, setPrereqLoading] = useState(false);
  const [drawerState, setDrawerState] = useState(null); // { avatar, missing }
  const [mobileTab, setMobileTab] = useState("conversa");
  const [artifactUpdated, setArtifactUpdated] = useState(false);
  const [threadsOpen, setThreadsOpen] = useState(false);
  const [threadQuery, setThreadQuery] = useState("");
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [newFolderTarget, setNewFolderTarget] = useState(null);
  const [newFolderValue, setNewFolderValue] = useState("");
  const [showIntro, setShowIntro] = useState(false);
  const [threadReady, setThreadReady] = useState(false);
  const [saveConfirm, setSaveConfirm] = useState(false);
  const [calOpen, setCalOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [stageOpen, setStageOpen] = useState(false);
  const scrollRef = useRef(null);

  const setActive = (t) => {
    activeThreadRef.current = t;
    setActiveThread(t);
  };
  const threadReadyRef = useRef(false);
  const markReady = (ready) => {
    threadReadyRef.current = ready;
    setThreadReady(ready);
  };
  const isCurrent = (seq) => seq === navSeqRef.current;

  const avatars = prereq?.avatars || [];
  const activeAvatar = avatars.find((a) => a.id === activeThread?.avatar_id) || null;

  // If the prerequisites endpoint isn't live yet (parallel backend work), derive
  // a minimal-but-correct prereq from GET /avatars so the gate still functions.
  const buildFallbackPrereq = async () => {
    try {
      const { data } = await api.get("/avatars", { params: { status: "complete" } });
      const list = data || [];
      return {
        requires_avatar: true,
        requires_brand_persona: true,
        user_has_avatar: list.length > 0,
        user_has_brand_persona: true,
        avatars: list,
        avatars_counts: { complete: list.length, draft: 0 },
        missing: [],
        cta: list.length ? null : "create_avatar",
      };
    } catch {
      return { requires_avatar: true, user_has_avatar: false, avatars: [], missing: [] };
    }
  };

  const loadAgent = async () => {
    const seq = ++navSeqRef.current;
    setLoading(true);
    setError(null);
    try {
      const [ag, ts, pr] = await Promise.all([
        api.get(`/agents/${slug}`),
        api.get(`/threads`, { params: { agent_slug: slug } }),
        api.get(`/agents/${slug}/prerequisites`).catch(() => ({ data: null })),
      ]);
      const prData = pr.data || (await buildFallbackPrereq());
      if (!isCurrent(seq)) return;
      setAgent(ag.data);
      setPrereq(prData);
      setThreads(ts.data || []);
      const wanted = threadParamRef.current
        ? (ts.data || []).find((t) => t.id === threadParamRef.current)
        : null;
      // Deep-linked thread not found: never autosend into a different thread.
      if (threadParamRef.current && !wanted) autosendRef.current = null;
      threadParamRef.current = null;
      if (wanted) {
        await openThread(wanted, ag.data, seq);
      } else if (ts.data?.length > 0) {
        await openThread(ts.data[0], ag.data, seq);
      } else if (!viewer) {
        // No conversation yet — start the mandatory picker flow.
        // A viewer can only read existing threads, never start one.
        beginNewConversation(prData, { seq });
      }
    } catch (e) {
      if (isCurrent(seq)) setError(friendlyError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAgent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  useEffect(() => {
    if (mobileTab === "artefato") setArtifactUpdated(false);
  }, [mobileTab]);

  const openThread = async (t, agentData, seq = navSeqRef.current, prevThread = activeThreadRef.current) => {
    markReady(false);
    setActive(t);
    try {
      const [ms, arts] = await Promise.all([
        api.get(`/threads/${t.id}/messages`),
        api.get(`/artifacts`, { params: { thread_id: t.id } }),
      ]);
      const ag = ms.data.length === 0 ? agentData || agent || (await api.get(`/agents/${slug}`)).data : null;
      if (!isCurrent(seq)) return;
      if (ms.data.length === 0) {
        setMessages([{ id: "greet", role: "assistant", content: ag.greeting }]);
        // Audio-first CTA on a fresh thread — never over a pending autosend.
        setShowIntro(!autosendRef.current);
      } else {
        // Rascunho ainda pendente: as ações reaparecem na última fala do
        // assistente, senão a decisão de salvar se perde ao reabrir a conversa.
        const pendente = (arts.data || []).find((a) => a.status === "draft");
        const msgs = [...ms.data];
        if (pendente) {
          for (let i = msgs.length - 1; i >= 0; i--) {
            if (msgs[i].role === "assistant") {
              msgs[i] = { ...msgs[i], draftArtifactId: pendente.id };
              break;
            }
          }
        }
        setMessages(msgs);
        setShowIntro(false);
      }
      setArtifact(arts.data[0] || null);
      setArtifactUpdated(false);
      setMobileTab("conversa");
      setThreadsOpen(false);
      markReady(true);
    } catch (err) {
      if (!isCurrent(seq)) return;
      // Restore the previous thread so the UI stays consistent; the sheet only
      // closes on success, so a failed click can be retried right there.
      autosendRef.current = null;
      toast.error(friendlyError(err));
      restoreThread(prevThread, seq);
    }
  };

  // Volta para a conversa anterior (picker cancelado, falha, gate de plano) com o envio liberado.
  const restoreThread = (prev, seq) => {
    if (!isCurrent(seq)) return;
    setActive(prev);
    markReady(true);
  };

  const refreshPrereq = async (seq = navSeqRef.current) => {
    setPrereqLoading(true);
    let data;
    try {
      data = (await api.get(`/agents/${slug}/prerequisites`)).data;
    } catch {
      data = await buildFallbackPrereq();
    } finally {
      setPrereqLoading(false);
    }
    if (isCurrent(seq)) setPrereq(data);
    return data;
  };

  const beginNewConversation = async (known, { forcePicker = false, seq = navSeqRef.current } = {}) => {
    if (viewer) return; // read-only: no thread creation in a shared workspace
    const pr = known || (await refreshPrereq(seq));
    if (!isCurrent(seq)) return;
    // Plan gate BEFORE any auto-created thread: a locked agent never POSTs
    // /threads (the backend would 403) — the existing locked flow stays in
    // charge. Tier comes from the prerequisites payload (or the loaded agent).
    const tier = pr?.agent?.tier ?? agent?.tier ?? "free";
    if ((PLAN_RANK[tier] ?? 0) > (PLAN_RANK[user?.plan] ?? 0)) {
      toast.error(LOCKED_PLAN_MSG);
      restoreThread(activeThreadRef.current, seq);
      return;
    }
    // Agents that don't require an avatar (edge case) skip the picker entirely.
    if (pr && pr.requires_avatar === false) {
      await createThread(null, seq);
      return;
    }
    // Exactly one complete avatar (17.5.5): skip the picker and go straight to
    // the thread — except when the "Copy para" pill forces the picker open to
    // switch avatars. The sheet remains the path for 0 (create CTA) or 2+ avatars.
    const list = pr?.avatars || [];
    const personaMissing = pr?.requires_brand_persona && !pr?.user_has_brand_persona;
    if (!forcePicker && list.length === 1 && pr?.user_has_avatar !== false && !personaMissing) {
      await selectAvatar(list[0], pr, seq);
      return;
    }
    setPickerOpen(true);
  };

  // "Nova conversa" (e a troca de avatar, que também abre uma conversa nova): a conversa
  // atual para de receber tokens e o envio fica travado até a nova estar pronta.
  const startNewConversation = (opts = {}) => {
    const seq = ++navSeqRef.current;
    abortRef.current?.abort();
    queuedRef.current = null;
    setQueued(false);
    markReady(false);
    beginNewConversation(undefined, { ...opts, seq });
  };

  const pickThread = (t) => {
    const seq = ++navSeqRef.current;
    abortRef.current?.abort();
    queuedRef.current = null;
    setQueued(false);
    openThread(t, undefined, seq);
  };

  const closePicker = (open) => {
    setPickerOpen(open);
    if (!open) restoreThread(activeThreadRef.current, navSeqRef.current);
  };

  const createThread = async (avatar, seq = navSeqRef.current) => {
    const prev = activeThreadRef.current;
    try {
      const newT = await api.post("/threads", {
        agent_slug: slug,
        avatar_id: avatar?.id || null,
      });
      if (!isCurrent(seq)) return;
      setThreads((arr) => [newT.data, ...arr]);
      setPickerOpen(false);
      if (searchParams.has("thread")) {
        setSearchParams((p) => {
          const next = new URLSearchParams(p);
          next.delete("thread");
          return next;
        }, { replace: true });
      }
      await openThread(newT.data, undefined, seq, prev);
    } catch (err) {
      if (!isCurrent(seq)) return;
      // Same friendly copy the Calendar uses for the plan gate — never a
      // generic error over an empty chat.
      if (err?.response?.status === 403 && errorCode(err) === "locked_plan") {
        toast.error(LOCKED_PLAN_MSG);
      } else {
        toast.error(friendlyError(err));
      }
      restoreThread(prev, seq);
    }
  };

  const selectAvatar = async (avatar, pr, seq = navSeqRef.current) => {
    // Only surface the drawer for fields the chosen avatar is actually missing.
    const miss = ((pr || prereq)?.missing || []).filter((m) =>
      m.scope === "avatar" ? !avatar[m.field] : true
    );
    if (miss.length) {
      setPickerOpen(false);
      setDrawerState({ avatar, missing: miss, newThread: true });
    } else {
      await createThread(avatar, seq);
    }
  };

  const onPickerSelect = (avatar) => { selectAvatar(avatar); };

  // Best-effort link back: the first artifact generated in this session gets
  // attached to the calendar entry that triggered the click-to-generate flow.
  const linkEntryArtifact = (artifactId) => {
    const entryId = entryRef.current;
    if (!entryId || entryLinkedRef.current || !artifactId) return;
    entryLinkedRef.current = true;
    api.patch(`/calendar/${entryId}`, { artifact_id: artifactId }).catch(() => {});
  };

  // Marca a bolha ATUAL do assistente como dona deste rascunho, para as ações
  // aparecerem exatamente onde o conteúdo nasceu (vale para texto e para voz).
  const attachDraftToLastBubble = (artifactId) => {
    setMessages((m) => {
      const next = [...m];
      for (let i = next.length - 1; i >= 0; i--) {
        if (next[i].role === "assistant") {
          next[i] = { ...next[i], draftArtifactId: artifactId, artifactDecision: null };
          break;
        }
      }
      return next;
    });
  };

  const saveDraftArtifact = async (messageId, artifactId) => {
    setSavingArtifactId(artifactId);
    try {
      const { data } = await api.post(`/artifacts/${artifactId}/save`);
      setArtifact(data);
      linkEntryArtifact(artifactId);
      setMessages((m) =>
        m.map((x) => (x.id === messageId ? { ...x, artifactDecision: "saved" } : x))
      );
      toast.success("Salvo na Biblioteca ✦");
    } catch (e) {
      toast.error(friendlyError(e));
    } finally {
      setSavingArtifactId(null);
    }
  };

  const keepAsDraft = (messageId) => {
    // Já nasce como rascunho: só registra a escolha e tira os botões da frente.
    setMessages((m) =>
      m.map((x) => (x.id === messageId ? { ...x, artifactDecision: "draft" } : x))
    );
  };

  const handleVoiceEvent = (evt) => {
    if (evt.type === "transcript") {
      setMessages((m) => {
        const next = [...m];
        const last = next[next.length - 1];
        if (last && last.role === evt.role && last.live) {
          next[next.length - 1] = { ...last, content: last.content + evt.text };
        } else {
          next.push({ id: `v-${Date.now()}-${Math.random()}`, role: evt.role, content: evt.text, live: true, via: "voice" });
        }
        return next;
      });
    } else if (evt.type === "turn_complete") {
      setMessages((m) => m.map((x) => (x.live ? { ...x, live: false } : x)));
    } else if (evt.type === "artifact") {
      setArtifact(evt.artifact);
      setArtifactUpdated(true);
      if (evt.artifact.status === "saved") linkEntryArtifact(evt.artifact.id);
      // O produto é audio-first: as ações do rascunho valem aqui igual ao texto.
      if (evt.artifact.status === "draft") attachDraftToLastBubble(evt.artifact.id);
      toast.success(
        evt.artifact.status === "saved"
          ? "Artefato salvo na Biblioteca ✦"
          : "Rascunho atualizado no painel."
      );
    } else if (evt.type === "error") {
      toast.error(evt.message || "A conversa por voz falhou. Tente novamente.");
    }
  };

  const onVoiceGate = (gate) => {
    if (gate?.missing?.length) {
      setDrawerState({ avatar: activeAvatar, missing: gate.missing });
    } else if (gate?.code) {
      toast.error(friendlyError({ response: { data: { detail: gate } } }));
    } else {
      toast.error("Não consegui iniciar a voz. Tente novamente.");
    }
  };

  const voice = useVoiceSession({
    agent: slug,
    threadId: activeThread?.id,
    onEvent: handleVoiceEvent,
    onGateError: onVoiceGate,
  });

  const startVoice = () => {
    setShowIntro(false);
    voice.dismissRecovery?.();
    voice.start();
  };
  const endVoice = () => { voice.stop(); };

  const attach = useThreadAttach({
    threadId: activeThread?.id,
    onAttached: (data) => {
      setMessages((m) => [...m, messageFromAttach(data)]);
    },
  });

  const downloadPdf = async () => {
    if (!artifact || pdfLoading) return;
    setPdfLoading(true);
    try {
      const view = workingView(artifact, true);
      await downloadArtifactPdf(artifact.id, pdfFallbackName(view), { working: true });
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setPdfLoading(false);
    }
  };

  const stopStreaming = () => {
    abortRef.current?.abort();
  };

  const sendMessage = async (contentArg, options = {}) => {
    if (viewer) return; // read-only guard: never write into a shared workspace
    const content = (contentArg ?? input).trim();
    const thread = activeThreadRef.current;
    if (!content || !thread || !threadReadyRef.current) return;

    // Streaming em curso: enfileira em vez de descartar em silêncio. O guard antigo
    // simplesmente sumia com o texto e a usuária lia isso como app travado.
    if (sending) {
      queuedRef.current = content;
      setQueued(true);
      if (contentArg == null || options.clearInput) setInput("");
      return;
    }

    // During an active voice session, text goes as a frame on the same socket
    // (FR17); the answer streams back as audio + transcript.
    if (voice.active && !options.forceText) {
      if (!voice.sendText(content)) {
        if (contentArg != null) setInput((current) => (current.trim() ? current : content));
        return false;
      }
      if (contentArg == null) setInput("");
      setShowIntro(false);
      setMessages((m) => [...m, { id: `u-${Date.now()}`, role: "user", content }]);
      return true;
    }

    if (contentArg == null || options.clearInput) setInput("");
    setShowIntro(false);
    setMessages((m) => [...m, { id: `u-${Date.now()}`, role: "user", content }]);
    setMessages((m) => [...m, { id: `a-${Date.now()}`, role: "assistant", content: "" }]);
    setSending(true);
    setOrbState("thinking");
    const controller = new AbortController();
    abortRef.current = controller;
    const seq = navSeqRef.current;
    try {
      await streamChat(thread.id, content, (evt) => {
        if (!isCurrent(seq)) return;
        if (evt.type === "token") {
          setOrbState("speaking");
          setMessages((m) => {
            const next = [...m];
            const i = next.length - 1;
            // `closed` marca a bolha encerrada por message_end: o próximo texto do
            // mesmo turno abre bolha nova em vez de grudar na anterior.
            if (next[i]?.role === "assistant" && !next[i].closed) {
              next[i] = { ...next[i], content: next[i].content + evt.content };
              return next;
            }
            return [...next, { id: `a-${Date.now()}-${next.length}`, role: "assistant", content: evt.content }];
          });
        } else if (evt.type === "message_end") {
          setMessages((m) => {
            const next = [...m];
            const i = next.length - 1;
            if (next[i]?.role === "assistant") next[i] = { ...next[i], closed: true };
            return next;
          });
        } else if (evt.type === "tool_call") {
          setOrbState("tool");
        } else if (evt.type === "tool_result") {
          if (
            evt.name === "schedule_calendar_entries" &&
            evt.result?.ok === true &&
            evt.result?.created > 0
          ) {
            scheduledRef.current = true;
          }
        } else if (evt.type === "artifact") {
          setArtifact(evt.artifact);
          setArtifactUpdated(true);
          if (evt.artifact.status === "saved") linkEntryArtifact(evt.artifact.id);
          if (evt.artifact.status === "draft") attachDraftToLastBubble(evt.artifact.id);
          toast.success(
            evt.artifact.status === "saved"
              ? "Artefato salvo na Biblioteca ✦"
              : "Rascunho registrado no painel."
          );
        } else if (evt.type === "error") {
          toast.error(evt.message || "A ValiosIA teve um problema ao responder. Tente de novo.");
          setOrbState("idle");
          setMessages((m) => m.filter((x) => !(x.role === "assistant" && !x.content)));
        } else if (evt.type === "done") {
          setOrbState("idle");
          if (scheduledRef.current) {
            scheduledRef.current = false;
            toast.success("Calendário criado ✦");
            setTimeout(() => nav("/calendario"), 1200);
          }
        }
      }, controller.signal);
    } catch (e) {
      if (isCurrent(seq)) setMessages((m) => m.filter((x) => !(x.role === "assistant" && !x.content)));
      if (e?.name === "AbortError") {
        setOrbState("idle");
      } else {
        toast.error(friendlyError(e));
        setOrbState("error");
      }
    } finally {
      // Um stream mais novo já assumiu o abortRef: o estado e a fila são dele.
      if (abortRef.current === controller) {
        abortRef.current = null;
        setSending(false);
        // Mensagem digitada durante o streaming sai agora, sozinha.
        const pending = queuedRef.current;
        if (pending) {
          queuedRef.current = null;
          setQueued(false);
          setTimeout(() => sendMessage(pending), 0);
        }
      }
    }
  };

  const continueVoiceByText = () => {
    const preserved = input.trim();
    voice.continueWithText?.();
    setShowIntro(false);
    if (preserved) sendMessage(preserved, { forceText: true, clearInput: true });
  };

  // Click-to-generate (Epic 15.4): fire the ?autosend message exactly once,
  // only AFTER the deep-linked thread finished loading its messages
  // (threadReady). Ref guard survives StrictMode's double effect invocation;
  // the URL is cleaned so refreshes don't re-send.
  useEffect(() => {
    const text = autosendRef.current;
    if (!text || viewer || !activeThread || !threadReady || autosentRef.current) return;
    autosentRef.current = true;
    autosendRef.current = null;
    setSearchParams({}, { replace: true });
    sendMessage(text);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeThread, threadReady, viewer]);

  const copyArtifact = async () => {
    const view = workingView(artifact, true);
    if (!view) return;
    try {
      await navigator.clipboard.writeText(view.content);
      toast.success("Conteúdo copiado.");
    } catch {
      toast.error("Não consegui copiar. Tente novamente.");
    }
  };
  const copyForInstagram = async () => {
    const view = workingView(artifact, true);
    if (!view) return;
    const caption = extractInstagramCaption(view.content);
    if (!caption) {
      toast.error("Este conteúdo não tem uma legenda pronta. Use Copiar para o roteiro.");
      return;
    }
    try {
      await navigator.clipboard.writeText(caption);
      toast.success("Legenda pronta pro Instagram ✦ É só colar.");
    } catch {
      toast.error("Não consegui copiar. Tente novamente.");
    }
  };
  const shareForInstagram = async () => {
    const view = workingView(artifact, true);
    if (!view) return;
    const caption = extractInstagramCaption(view.content) || view.content;
    try {
      await navigator.share({ title: view.title || "Conteúdo", text: caption });
    } catch {
      /* user dismissed the native share sheet — no-op */
    }
  };
  const redoArtifact = () => {
    if (!artifact || sending) return;
    sendMessage("Pode refazer com outra abordagem?");
  };

  const detachDoc = async (doc) => {
    if (!activeThread?.id || !doc?.id) return;
    try {
      await api.delete(`/threads/${activeThread.id}/attach/${doc.id}`);
      setMessages((m) => m.filter((x) => x.id !== doc.id));
    } catch (err) {
      toast.error(friendlyError(err));
    }
  };
  const savingRevision = isSavedWithRevision(artifact);
  const confirmSave = async () => {
    setSaveConfirm(false);
    if (!artifact || sending) return;
    if (isSavedWithRevision(artifact)) {
      if (savingArtifactId) return;
      setSavingArtifactId(artifact.id);
      try {
        const { data } = await api.post(`/artifacts/${artifact.id}/save`);
        setArtifact(data);
        toast.success("Ajustes salvos na Biblioteca ✦");
      } catch (e) {
        toast.error(friendlyError(e));
      } finally {
        setSavingArtifactId(null);
      }
      return;
    }
    if (artifact.status === "saved") return;
    sendMessage("Pode salvar como final.");
  };

  // --- Thread management (rename / move to folder / delete) ---------------
  const patchThread = async (t, payload) => {
    const { data } = await api.patch(`/threads/${t.id}`, payload);
    setThreads((arr) => arr.map((x) => (x.id === t.id ? { ...x, ...data } : x)));
    if (activeThreadRef.current?.id === t.id) setActive({ ...activeThreadRef.current, ...data });
    return data;
  };

  const submitRename = async () => {
    const title = renameValue.trim();
    if (!renameTarget || !title) { setRenameTarget(null); return; }
    try {
      await patchThread(renameTarget, { title });
      toast.success("Conversa renomeada.");
    } catch (err) {
      toast.error(friendlyError(err));
    }
    setRenameTarget(null);
  };

  const moveToFolder = async (t, folder) => {
    try {
      await patchThread(t, { folder });
      toast.success(folder ? `Movida para “${folder}”.` : "Removida da pasta.");
    } catch (err) {
      toast.error(friendlyError(err));
    }
  };

  const submitNewFolder = async () => {
    const name = newFolderValue.trim();
    if (!newFolderTarget || !name) { setNewFolderTarget(null); return; }
    await moveToFolder(newFolderTarget, name);
    setNewFolderTarget(null);
  };

  const confirmDeleteThread = async () => {
    const t = deleteTarget;
    setDeleteTarget(null);
    if (!t) return;
    try {
      await api.delete(`/threads/${t.id}`);
      const remaining = threads.filter((x) => x.id !== t.id);
      setThreads(remaining);
      toast.success("Conversa excluída.");
      if (activeThread?.id === t.id) {
        if (remaining.length) await openThread(remaining[0]);
        else {
          setActive(null);
          setMessages([]);
          setArtifact(null);
          setThreadsOpen(false);
          await beginNewConversation();
        }
      }
    } catch (err) {
      toast.error(friendlyError(err));
    }
  };

  const filteredThreads = threads.filter((t) =>
    !threadQuery || (t.title || "").toLowerCase().includes(threadQuery.toLowerCase())
  );
  const folders = [...new Set(threads.map((t) => t.folder).filter(Boolean))].sort((a, b) =>
    a.localeCompare(b, "pt-BR")
  );
  // Folder groups first (alphabetical), unfoldered threads last — headers only
  // appear once at least one folder exists.
  const threadGroups = [
    ...folders
      .map((f) => ({ folder: f, items: filteredThreads.filter((t) => t.folder === f) }))
      .filter((g) => g.items.length),
    { folder: null, items: filteredThreads.filter((t) => !t.folder) },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-bg flex flex-col overflow-x-clip" data-testid="agent-chat-page">
        <AppHeader />
        <div className="flex-1 max-w-[1400px] mx-auto w-full px-4 lg:px-8 py-6 pb-24 md:pb-6">
          <CardGridSkeleton count={3} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-brand-bg flex flex-col overflow-x-clip" data-testid="agent-chat-page">
        <AppHeader />
        <div className="flex-1 flex items-center justify-center px-6 pb-24 md:pb-0">
          <div className="max-w-md w-full">
            <ErrorState message={error} onRetry={loadAgent} testid="agent-error" />
          </div>
        </div>
      </div>
    );
  }

  const orbActiveState = voice.active ? voice.status : orbState;

  // --- Chat panel ---------------------------------------------------------
  const chatPanel = (
    <section
      className={`bg-brand-surface border border-brand-line rounded-3xl flex-col overflow-hidden relative ${
        mobileTab === "conversa" ? "flex" : "hidden"
      } lg:flex h-[calc(100vh-215px)] lg:h-auto`}
      data-testid="chat-panel"
    >
      <div className="flex items-center gap-3 px-4 lg:px-6 py-3.5 border-b border-brand-line">
        <VoiceOrb
          state={orbActiveState}
          size={44}
          showLabel={false}
          levels={voice.active ? voice.levels : null}
        />
        <div className="flex-1 min-w-0">
          <div className="font-display text-lg text-brand-ink truncate">{agent?.nome}</div>
          <div className="text-[12px] text-brand-ink2 font-ui truncate">
            {voice.active ? (
              <span className="inline-flex items-center gap-1.5 text-brand-sage">
                <span className="w-1.5 h-1.5 rounded-full bg-brand-sage" aria-hidden />
                No ar · conversa contínua
              </span>
            ) : agent?.descricao}
          </div>
        </div>
        {activeAvatar && (
          viewer ? (
            <span
              data-testid="agent-avatar-pill"
              className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-brand-elevated border border-brand-line text-[12px] text-brand-ink2 font-ui max-w-[52vw] sm:max-w-none"
            >
              <span className="truncate">Público: {publicAvatarDescription(activeAvatar, avatars)}</span>
            </span>
          ) : (
            <button
              type="button"
              data-testid="agent-avatar-pill"
              onClick={() => startNewConversation({ forcePicker: true })}
              title="Trocar avatar (nova conversa)"
              className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-brand-elevated border border-brand-line text-[12px] text-brand-ink2 hover:text-brand-rose hover:border-brand-rose transition-colors font-ui max-w-[52vw] sm:max-w-none"
            >
              <span className="truncate">Público: {publicAvatarDescription(activeAvatar, avatars)}</span>
              <Repeat className="w-3.5 h-3.5 shrink-0" />
            </button>
          )
        )}
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto overflow-x-hidden px-4 lg:px-6 py-5 space-y-2.5 scrollbar-soft" data-testid="chat-feed">
        {messages.map((m) => (
          <div key={m.id}>
            <MessageBubble message={m} />
            {m.via === "voice" && (
              <div className={`mt-0.5 text-[10.5px] text-brand-ink3 font-ui flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <span className="inline-flex items-center gap-1">✦ por voz</span>
              </div>
            )}
            {/* O rascunho nasce nesta bolha: as ações ficam aqui, onde ela está
                olhando — não num painel lateral que pode nem estar aberto. */}
            {!viewer && m.draftArtifactId && (
              <ArtifactActions
                saving={savingArtifactId === m.draftArtifactId}
                done={m.artifactDecision || null}
                onSave={() => saveDraftArtifact(m.id, m.draftArtifactId)}
                onKeepDraft={() => keepAsDraft(m.id)}
              />
            )}
          </div>
        ))}
      </div>

      <div>
        <ChatComposer
          value={input}
          onChange={setInput}
          onSend={() => sendMessage()}
          onStop={stopStreaming}
          sending={sending}
          queued={queued}
          placeholder={
            viewer
              ? "Somente leitura — volte à sua conta para escrever."
              : voice.active
                ? "Digite mesmo durante a voz…"
                : `Fale com ${agent?.nome || "a ValiosIA"}…`
          }
          onFiles={viewer || !activeThread ? undefined : attach.attachFiles}
          attachItems={attach.items}
          threadDocs={docsFromMessages(messages)}
          onUseDoc={viewer ? undefined : (doc) => sendMessage(promptToUseDoc(doc.filename))}
          onDetachDoc={viewer ? undefined : detachDoc}
          attaching={attach.attaching}
          fileInputRef={attach.inputRef}
          onMicToggle={viewer ? undefined : () => (voice.active ? endVoice() : startVoice())}
          voiceActive={voice.active}
          voiceStatus={voice.status}
          disabled={!activeThread || !threadReady || viewer}
          testids={{ input: "chat-input", send: "chat-send", voice: "chat-voice-toggle", attach: "chat-attach" }}
        />
        {voice.active && !voice.recovery && !viewer && (
          <VoiceHelpAction onClick={voice.reportAudioProblem} />
        )}
      </div>

      {voice.recovery && activeThread && !viewer && (
        <AudioFirstOverlay
          title={voice.recovery.title}
          description={voice.recovery.message}
          onVoice={voice.resume}
          onText={continueVoiceByText}
          voiceLabel={voice.recovery.actionLabel || "Retomar voz"}
          textLabel="Continuar por texto"
          recovery
        />
      )}

      {showIntro && !voice.recovery && activeThread && messages.length <= 1 && !voice.active && !viewer && (
        <AudioFirstOverlay
          title={`Conversar com ${agent?.nome || "a ValiosIA"}?`}
          description="É uma conversa contínua, não uma gravação. Fale naturalmente — a ValiosIA responde ao vivo. Troca para texto quando quiser."
          onVoice={startVoice}
          onText={() => setShowIntro(false)}
        />
      )}
    </section>
  );

  // --- Artifact panel -----------------------------------------------------
  const artifactPanel = (
    <aside
      className={`bg-brand-surface border border-brand-line rounded-3xl flex-col overflow-hidden ${
        mobileTab === "artefato" ? "flex" : "hidden"
      } lg:flex h-[calc(100vh-215px)] lg:h-auto`}
      data-testid="artifact-pane"
    >
      <div className="flex items-center justify-between px-4 lg:px-6 py-4 border-b border-brand-line">
        <div className="min-w-0">
          <div className="text-[10.5px] tracking-[0.22em] uppercase text-brand-ink2 font-ui">
            Artefato {artifact?.status === "saved" && <span className="text-brand-sage">(salvo)</span>}
          </div>
          <div className="font-display text-lg text-brand-ink truncate">
            {workingView(artifact, true)?.title || "Aguardando geração"}
          </div>
        </div>
        <div className="flex gap-1 items-center">
          {artifact?.status === "saved" && !viewer && (
            <button data-testid="artifact-add-calendar" onClick={() => setCalOpen(true)} className="p-2 rounded-full hover:bg-brand-elevated text-brand-ink2 hover:text-brand-rose" title="Adicionar ao calendário" aria-label="Adicionar ao calendário">
              <CalendarPlus className="w-4 h-4" />
            </button>
          )}
          <ArtifactToolbar
            artifact={workingView(artifact, true)}
            compact
            viewer={viewer}
            sending={sending}
            saving={Boolean(artifact) && savingArtifactId === artifact.id}
            pdfLoading={pdfLoading}
            onCopy={copyArtifact}
            onInstagram={copyForInstagram}
            onShare={shareForInstagram}
            onRedo={redoArtifact}
            onSave={() => setSaveConfirm(true)}
            onPdf={downloadPdf}
            onExpand={() => setStageOpen(true)}
          />
        </div>
      </div>
      {artifact && <TablesBar artifact={artifact} working />}
      <div className="flex-1 overflow-y-auto overflow-x-hidden px-4 lg:px-6 py-5 scrollbar-soft" data-testid="artifact-content">
        {artifact ? (
          artifact.type === "carrossel" ? (
            <CarouselArtifact content={workingView(artifact, true)?.content} />
          ) : (
            <Markdown text={workingView(artifact, true)?.content} />
          )
        ) : (
          <div className="text-center text-brand-ink2 mt-20" data-testid="artifact-empty">
            <p className="text-[14px] mb-2">O conteúdo gerado vai aparecer aqui.</p>
            <p className="text-[13px] text-brand-sand">Converse com o agente para começar.</p>
          </div>
        )}
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen bg-brand-bg flex flex-col overflow-x-clip" data-testid="agent-chat-page">
      <AppHeader />
      <div className="flex-1 max-w-[1400px] mx-auto w-full px-3 sm:px-4 lg:px-8 py-4 lg:py-6 pb-24 md:pb-6">
        {/* Action row: threads sheet + new conversation */}
        <div className="flex items-center justify-between gap-2 mb-3">
          <button
            data-testid="open-threads"
            onClick={() => setThreadsOpen(true)}
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-brand-surface border border-brand-line text-[12.5px] text-brand-ink2 hover:text-brand-rose hover:border-brand-rose transition-colors font-ui min-h-[40px]"
          >
            <History className="w-4 h-4" /> Conversas
          </button>
          {!viewer && (
            <button
              data-testid="new-thread"
              onClick={() => startNewConversation()}
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-brand-rose hover:bg-brand-roseHover text-white text-[12.5px] transition-colors font-ui min-h-[40px]"
            >
              <Plus className="w-4 h-4" /> Nova conversa
            </button>
          )}
        </div>

        {/* Mobile tabs (shadcn) */}
        <Tabs value={mobileTab} onValueChange={setMobileTab} className="lg:hidden mb-3">
          <TabsList className="w-full grid grid-cols-2">
            <TabsTrigger value="conversa" data-testid="tab-conversa">Conversa</TabsTrigger>
            <TabsTrigger value="artefato" data-testid="tab-artefato" className="relative">
              Artefato
              {artifactUpdated && mobileTab !== "artefato" && (
                <span data-testid="artifact-badge" className="absolute top-1.5 right-3 w-2 h-2 rounded-full bg-brand-rose animate-pulse" />
              )}
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* min-w-0: sem isso uma tabela larga do artefato estica a coluna do grid
            (min-width:auto) e a página inteira rola de lado. */}
        <div className="lg:grid lg:grid-cols-[1fr_1.1fr] lg:gap-5 lg:h-[calc(100vh-150px)] [&>*]:min-w-0">
          {chatPanel}
          {artifactPanel}
        </div>
      </div>

      {/* Threads sheet (any viewport) */}
      <Sheet open={threadsOpen} onOpenChange={setThreadsOpen}>
        <SheetContent side="left" data-testid="threads-sheet" className="bg-brand-surface border-brand-line w-[85vw] sm:max-w-sm">
          <SheetHeader className="text-left">
            <SheetTitle className="font-display text-2xl text-brand-ink">Conversas</SheetTitle>
          </SheetHeader>
          <div className="relative mt-4 mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-ink3" />
            <input
              data-testid="threads-search"
              value={threadQuery}
              onChange={(e) => setThreadQuery(e.target.value)}
              placeholder="Buscar…"
              className="w-full pl-9 pr-3 py-2.5 rounded-full bg-brand-elevated border border-brand-line focus:border-brand-rose focus:outline-none text-[16px] text-white placeholder:text-brand-ink3"
            />
          </div>
          <div className="space-y-1 overflow-y-auto overflow-x-hidden scrollbar-soft max-h-[70vh]">
            {threadGroups.map(({ folder, items }) => (
              <div key={folder || "__sem_pasta__"}>
                {folders.length > 0 && items.length > 0 && (
                  <div
                    data-testid={`thread-folder-group-${folder || "sem-pasta"}`}
                    className="flex items-center gap-1.5 px-3 pt-3 pb-1 text-[10.5px] tracking-[0.18em] uppercase text-brand-ink3 font-ui"
                  >
                    <Folder className="w-3 h-3" />
                    {folder || "Sem pasta"}
                  </div>
                )}
                {items.map((t) => {
                  const active = activeThread?.id === t.id;
                  const savedTitle = t.saved_artifact_title;
                  return (
                    <div
                      key={t.id}
                      className={`group flex items-center rounded-xl transition-colors ${
                        active ? "bg-brand-elevated" : "hover:bg-brand-elevated/50"
                      }`}
                    >
                      <button
                        data-testid={`thread-item-${t.id}`}
                        onClick={() => pickThread(t)}
                        className="flex-1 min-w-0 text-left px-3 py-2.5"
                      >
                        {savedTitle ? (
                          <span className="flex items-center gap-2 min-w-0" data-testid="thread-has-artifact">
                            <FileText className="w-3.5 h-3.5 text-brand-rose shrink-0" />
                            <span className={`text-[13px] truncate ${active ? "text-brand-ink" : "text-brand-ink2"}`}>
                              {savedTitle}
                            </span>
                          </span>
                        ) : (
                          <span className="block min-w-0">
                            <span className={`block text-[13px] truncate ${active ? "text-brand-ink" : "text-brand-ink2"}`}>
                              {t.title}
                            </span>
                            <span className="block text-[11px] text-brand-ink3 font-ui">sem conteúdo salvo</span>
                          </span>
                        )}
                      </button>
                      {!viewer && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              data-testid={`thread-menu-${t.id}`}
                              aria-label={`Opções da conversa ${t.title}`}
                              className="shrink-0 w-9 h-9 mr-1 rounded-full flex items-center justify-center text-brand-ink3 hover:text-white hover:bg-brand-bg/60 transition-colors"
                            >
                              <MoreVertical className="w-4 h-4" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-56 bg-brand-surface border-brand-line">
                            <DropdownMenuItem
                              data-testid="thread-rename"
                              className="focus:bg-brand-elevated cursor-pointer text-brand-ink"
                              onClick={() => { setRenameTarget(t); setRenameValue(t.title || ""); }}
                            >
                              <Pencil className="w-4 h-4 mr-2 text-brand-rose" /> Renomear
                            </DropdownMenuItem>
                            <DropdownMenuSub>
                              <DropdownMenuSubTrigger
                                data-testid="thread-move-folder"
                                className="focus:bg-brand-elevated cursor-pointer text-brand-ink"
                              >
                                <FolderInput className="w-4 h-4 mr-2 text-brand-rose" /> Mover para pasta
                              </DropdownMenuSubTrigger>
                              <DropdownMenuSubContent className="w-52 bg-brand-surface border-brand-line">
                                {folders.map((f) => (
                                  <DropdownMenuItem
                                    key={f}
                                    data-testid={`thread-folder-option-${f}`}
                                    disabled={t.folder === f}
                                    className="focus:bg-brand-elevated cursor-pointer text-brand-ink"
                                    onClick={() => moveToFolder(t, f)}
                                  >
                                    <Folder className="w-4 h-4 mr-2 text-brand-ink3" /> {f}
                                  </DropdownMenuItem>
                                ))}
                                {folders.length > 0 && <DropdownMenuSeparator className="bg-brand-line" />}
                                <DropdownMenuItem
                                  data-testid="thread-folder-new"
                                  className="focus:bg-brand-elevated cursor-pointer text-brand-ink"
                                  onClick={() => { setNewFolderTarget(t); setNewFolderValue(""); }}
                                >
                                  <FolderPlus className="w-4 h-4 mr-2 text-brand-rose" /> Nova pasta…
                                </DropdownMenuItem>
                                {t.folder && (
                                  <DropdownMenuItem
                                    data-testid="thread-folder-remove"
                                    className="focus:bg-brand-elevated cursor-pointer text-brand-ink"
                                    onClick={() => moveToFolder(t, null)}
                                  >
                                    <FolderMinus className="w-4 h-4 mr-2 text-brand-ink3" /> Remover da pasta
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuSubContent>
                            </DropdownMenuSub>
                            <DropdownMenuSeparator className="bg-brand-line" />
                            <DropdownMenuItem
                              data-testid="thread-delete"
                              className="focus:bg-brand-elevated cursor-pointer text-red-400 focus:text-red-400"
                              onClick={() => setDeleteTarget(t)}
                            >
                              <Trash2 className="w-4 h-4 mr-2" /> Excluir
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
            {filteredThreads.length === 0 && (
              <div className="text-[13px] text-brand-ink2 py-6 text-center">Nenhuma conversa ainda.</div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Mandatory avatar picker */}
      <AvatarPickerSheet
        open={pickerOpen}
        onOpenChange={closePicker}
        prereq={prereq}
        loading={prereqLoading}
        onSelect={onPickerSelect}
        onCreateAvatar={async () => {
          setPickerOpen(false);
          try { await startAvatarCreation({ nav }); } catch (err) { toast.error(friendlyError(err)); }
        }}
        onCreatePersona={() => { setPickerOpen(false); nav("/onboarding/brand-persona"); }}
      />

      {/* Inline required-fields resolution */}
      <RequiredFieldsDrawer
        open={Boolean(drawerState)}
        onOpenChange={(o) => {
          if (o) return;
          const pending = drawerState;
          setDrawerState(null);
          if (pending?.newThread) restoreThread(activeThreadRef.current, navSeqRef.current);
        }}
        agentSlug={slug}
        avatar={drawerState?.avatar}
        missing={drawerState?.missing || []}
        onResolved={async (avatar) => {
          const pending = drawerState;
          const seq = navSeqRef.current;
          setDrawerState(null);
          await refreshPrereq(seq);
          if (pending?.newThread || !activeThreadRef.current) { await createThread(avatar, seq); }
          else { toast.success("Tudo certo! Pode continuar ✦"); }
        }}
      />

      {/* Save confirmation (FR31) */}
      <AlertDialog open={saveConfirm} onOpenChange={setSaveConfirm}>
        <AlertDialogContent data-testid="artifact-save-dialog" className="bg-brand-surface border-brand-line">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl text-brand-ink">
              {savingRevision ? "Salvar ajustes?" : "Salvar na Biblioteca?"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-brand-ink2">
              {savingRevision
                ? "A versão ajustada substitui a salva na sua Biblioteca."
                : "O artefato fica salvo e disponível na sua Biblioteca."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-brand-elevated border-brand-line text-brand-ink2">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="artifact-save-confirm" onClick={confirmSave} className="bg-brand-rose hover:bg-brand-roseHover text-white">
              Salvar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Thread management dialogs */}
      <Dialog open={!!renameTarget} onOpenChange={(o) => !o && setRenameTarget(null)}>
        <DialogContent data-testid="thread-rename-dialog" className="bg-brand-surface border-brand-line max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-brand-ink">Renomear conversa</DialogTitle>
          </DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); submitRename(); }}>
            <input
              data-testid="thread-rename-input"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              maxLength={200}
              autoFocus
              className="w-full px-4 py-3 rounded-xl bg-brand-elevated border border-brand-line focus:border-brand-rose focus:outline-none text-[16px] text-white"
            />
            <DialogFooter className="mt-5">
              <button
                type="button"
                onClick={() => setRenameTarget(null)}
                className="px-5 py-2.5 rounded-full bg-brand-elevated border border-brand-line text-brand-ink2 text-[13.5px] font-ui min-h-[44px]"
              >
                Cancelar
              </button>
              <button
                type="submit"
                data-testid="thread-rename-save"
                disabled={!renameValue.trim()}
                className="pill-pink px-6 py-2.5 text-[13.5px] font-ui disabled:opacity-50 min-h-[44px]"
              >
                Renomear
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!newFolderTarget} onOpenChange={(o) => !o && setNewFolderTarget(null)}>
        <DialogContent data-testid="thread-folder-dialog" className="bg-brand-surface border-brand-line max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-brand-ink">Nova pasta</DialogTitle>
          </DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); submitNewFolder(); }}>
            <input
              data-testid="thread-folder-input"
              value={newFolderValue}
              onChange={(e) => setNewFolderValue(e.target.value)}
              maxLength={100}
              placeholder="Nome da pasta"
              autoFocus
              className="w-full px-4 py-3 rounded-xl bg-brand-elevated border border-brand-line focus:border-brand-rose focus:outline-none text-[16px] text-white placeholder:text-brand-ink3"
            />
            <DialogFooter className="mt-5">
              <button
                type="button"
                onClick={() => setNewFolderTarget(null)}
                className="px-5 py-2.5 rounded-full bg-brand-elevated border border-brand-line text-brand-ink2 text-[13.5px] font-ui min-h-[44px]"
              >
                Cancelar
              </button>
              <button
                type="submit"
                data-testid="thread-folder-save"
                disabled={!newFolderValue.trim()}
                className="pill-pink px-6 py-2.5 text-[13.5px] font-ui disabled:opacity-50 min-h-[44px]"
              >
                Criar e mover
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="thread-delete-dialog" className="bg-brand-surface border-brand-line">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl text-brand-ink">Excluir conversa?</AlertDialogTitle>
            <AlertDialogDescription className="text-brand-ink2">
              As mensagens desta conversa serão apagadas. Artefatos já salvos na
              Biblioteca continuam lá.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-brand-elevated border-brand-line text-brand-ink2">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              data-testid="thread-delete-confirm"
              onClick={confirmDeleteThread}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AddToCalendarSheet
        open={calOpen}
        onOpenChange={setCalOpen}
        preselectedArtifact={artifact}
      />

      <ArtifactStage
        open={stageOpen}
        onClose={() => setStageOpen(false)}
        artifact={workingView(artifact, true)}
        viewer={viewer}
        sending={sending}
        saving={Boolean(artifact) && savingArtifactId === artifact.id}
        pdfLoading={pdfLoading}
        onCopy={copyArtifact}
        onInstagram={copyForInstagram}
        onShare={shareForInstagram}
        onRedo={redoArtifact}
        onSave={() => setSaveConfirm(true)}
        onPdf={downloadPdf}
        working
      />

    </div>
  );
}
